
import { Component, Vue } from 'vue-property-decorator'
import keyCodeMapperCharacter from '@/utils/keycodemap.utils'
import * as onScan from 'onscan.js'
import OrderWarehouseProvider from '@/resources/order-warehouse.provider'
import StockLocationProvider from '@/resources/stock-location.provider'
import { mapState, mapActions } from 'vuex'

import PickHeader from './components/pick-header.vue'
import PickFooter from './components/pick-footer.vue'
import PickStep from './components/pick-step.vue'
import SummaryStep from './components/summary.vue'

@Component({
  components: {
    PickHeader,
    PickFooter,
    PickStep,
    SummaryStep
  },
  computed: {
    ...mapState({
      selectBranch: (state: any) => state.Store.selectBranch,
      products: (state: any) => state.FastPick.products,
      orders: (state: any) => state.FastPick.orders,
      withdrawNotes: (state: any) => state.FastPick.withdrawNotes,
      currentStep: (state: any) => state.FastPick.currentStep,
      pickPage: (state: any) => state.FastPick.pickPage,
      currentLocation: (state: any) => state.FastPick.currentLocation
    })
  },
  methods: {
    ...mapActions({
      setProduct: 'FastPick/setProduct',
      addProduct: 'FastPick/addProduct',
      removeProduct: 'FastPick/removeProduct',
      setOrders: 'FastPick/setOrders',
      addOrder: 'FastPick/addOrder',
      removeOrder: 'FastPick/removeOrder',
      setCurrentStep: 'FastPick/setCurrentStep',
      setPickPage: 'FastPick/setPickPage',
      setCurrentLocation: 'FastPick/setCurrentLocation',
      setDefault: 'FastPick/setDefault',
      decrementLocationCount: 'FastPick/decrementLocationCount',
      addProductIndexQty: 'FastPick/addProductIndexQty',
      addProductIndexOrder: 'FastPick/addProductIndexOrder',
      incrementProductIndexCount: 'FastPick/incrementProductIndexCount'
    })
  }
})
export default class PickOrder extends Vue {
  // --- STATE ---
  products!: any

  orders!: any

  withdrawNotes!: any

  selectBranch!: any

  currentStep!: any

  pickPage!: any

  currentLocation!: any

  setProduct!: any

  addProduct!: any

  removeProduct!: any

  setOrders!: any

  addOrder!: any

  removeOrder!: any

  setCurrentStep!: any

  setPickPage!: any

  setCurrentLocation!: any

  setDefault!: any

  decrementLocationCount!: any

  addProductIndexQty!: any

  addProductIndexOrder!: any

  incrementProductIndexCount!: any

  loadingOrder: any = false

  loadingLocation: any = false

  // --- SERVICES (Data Logic) ---
  orderWarehouseProvider = new OrderWarehouseProvider()

  stockLocationService = new StockLocationProvider()

  // --- COMPUTED (State Calculation) ---
  get totalSkuAndTotalQty (): any {
    return {
      totalSku: this.products.length,
      totalQty: this.products.reduce((sum: number, item: any) => sum + item.qty, 0),
      totalCount: this.products.reduce((sum: number, item: any) => sum + item.count, 0)
    }
  }

  // --- LIFECYCLE / SCANNER LOGIC ---
  async mounted (): Promise<void> {
    if ((this.currentStep === 'start' && this.orders.length) || this.withdrawNotes.length) {
      this.setDefault()
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this
    onScan.attachTo(document, {
        suffixKeyCodes: [13],
        reactToPaste: true,
        onScan (scanCode) {
          const sCode = scanCode.trim()
          const patternUrl = /^(https:\/\/www\.gentlewomanonline\.com\/|https:\/\/gentlewomanonline\.com\/|https:\/\/www\.matter-makers\.com\/|https:\/\/matter-makers\.com\/)/
          const patternOrder = /^(GW|MM)\d+-\d+$/
          const patternLocation = /^location::/
          if (patternLocation.test(sCode)) {
            _this.getLocation(sCode.split('::')[1])
          } else if (patternUrl.test(sCode.toLowerCase())) {
            const splitCodeFromUrl = sCode.split('/')
            const code = splitCodeFromUrl[splitCodeFromUrl.length - 1]
            if (code) _this.incrementCounter(code)
          } else if (patternOrder.test(sCode)) {
            _this.getOrderDetail(sCode)
          } else {
            _this.$buefy.toast.open({ message: 'QR Code นี้ไม่สามารถใช้งานได้', type: 'is-danger' })
          }
        },
        keyCodeMapper (oEvent: any) {
          if (keyCodeMapperCharacter[+oEvent.keyCode] !== undefined) {
            return keyCodeMapperCharacter[+oEvent.keyCode][+oEvent.shiftKey]
          }
          return onScan.decodeKeyEvent(oEvent)
        }
    })
  }

  // eslint-disable-next-line class-methods-use-this
  beforeDestroy (): void {
    onScan.detachFrom(document)
  }

  // --- METHODS (Business Logic/State Manipulation) ---

  async getLocation (locationId: string): Promise<void> {
    if (this.currentStep !== 'pick') {
      this.$buefy.toast.open({ message: 'กรุณาสแกน QR Code ของ Order', type: 'is-danger' })
      return
    }
    try {
      this.loadingLocation = true
      const { data: scanLocation } = await this.stockLocationService.getLocationById(locationId, { warehouse: this.selectBranch.warehouse.id })
      if (!scanLocation) {
        this.$buefy.toast.open({ message: 'ไม่มีตำแหน่งนี้ใน warehouse ที่คุณเลือก', type: 'is-danger' })
        return
      }
      const newLocationIsExistingInMapping = this.products[this.pickPage].locations.findIndex((lo: any) => lo.location.id === scanLocation?.id)
      if (newLocationIsExistingInMapping !== -1) {
        this.setCurrentLocation(scanLocation)
      } else {
        this.$buefy.toast.open({ message: 'ไม่มีสินค้าอยู่ในตำแหน่งนี้', type: 'is-danger' })
      }
    } catch (error) {
      this.$buefy.toast.open({ message: 'การดึงข้อมูล location ผิดพลาด', type: 'is-danger' })
    } finally {
      this.loadingLocation = false
    }
  }

  async getOrderDetail (orderId: string): Promise<void> {
    if (this.currentStep !== 'start') {
      this.$buefy.toast.open({ message: 'ไม่สามารถเพิ่มออเดอร์ระหว่างหยิบได้', type: 'is-danger' })
      return
    }
    const isDuplicateOrder = this.orders.some((order: string) => order === orderId)
    if (isDuplicateOrder) {
      this.$buefy.toast.open({ message: 'มีออเดอร์นี้อยู่ในรายการแล้ว', type: 'is-danger' })
      return
    }
    try {
      this.loadingOrder = true
      const { data: orderInfo } = await this.orderWarehouseProvider.getOrderById(orderId)
      if (!orderInfo) {
        this.$buefy.toast.open({ message: 'ออเดอร์ไม่ถูกต้อง', type: 'is-danger' })
      } else if (orderInfo.warehouse.id !== this.selectBranch.warehouse.id) {
        this.$buefy.toast.open({ message: 'ออเดอร์นี้ไม่ใช่ของสาขาที่คุณเลือก', type: 'is-danger' })
      } else if (orderInfo.currentState !== 'ready_to_ship') {
        this.$buefy.toast.open({ message: 'ออเดอร์นี้ไม่อยู่ในสถานะที่ทำการ pick ได้', type: 'is-danger' })
      } else {
        this.addOrder(orderId)
        orderInfo.skus.forEach(async (sku: any) => {
          const existingProductIndex = this.products.findIndex((p: any) => p.code === sku.code)
          if (existingProductIndex === -1) {
            const productLocations = await this.getProductLocation(sku.code) ?? []
            this.addProduct({
              id: sku.id,
              code: sku.code,
              images: sku.images,
              price: sku.price,
              option: '',
              name: sku.name,
              collection: '',
              size: sku.size,
              color: sku.color,
              qty: sku.amount,
              locations: productLocations,
              count: 0,
              orders: [
                { orderId, qty: sku.amount }
              ]
            })
          } else {
            this.addProductIndexQty({
              productIndex: existingProductIndex,
              qty: sku.amount
            })
            this.addProductIndexOrder({
              productIndex: existingProductIndex,
              orderId,
              qty: sku.amount
            })
          }
        })
      }
    } catch (error) {
      this.$buefy.toast.open({ message: 'การดึงข้อมูลออเดอร์ผิดพลาด', type: 'is-danger' })
    } finally {
      this.loadingOrder = false
    }
  }

  incrementCounter (code: string): void {
    if (this.currentStep === 'start') {
      this.$buefy.toast.open({ message: 'กรุณาสแกน QR Code ของ Order', type: 'is-danger' })
    } else if (this.currentStep === 'summary') {
      this.$buefy.toast.open({ message: 'หากต้องการหยิบต่อกรุณากดปุ่ม Back', type: 'is-danger' })
    } else if (!this.currentLocation) {
      this.$buefy.toast.open({ message: 'กรุณาสแกน QR Code ของ ตำแหน่งที่คุณอยู่', type: 'is-danger' })
    } else if (this.products[this.pickPage] && this.products[this.pickPage].code !== code) {
      this.$buefy.toast.open({ message: 'ไม่ใช่รายการสินค้าในหน้านี้', type: 'is-danger' })
    } else if (this.products[this.pickPage].qty === this.products[this.pickPage].count) {
      this.$buefy.toast.open({ message: 'จำนวนสินค้าในหน้านี้ครบแล้วกรุณาตรวจสอบ', type: 'is-danger' })
    } else {
      const locationIndex = this.products[this.pickPage].locations.findIndex((lo: any) => lo.location.id === this.currentLocation?.id)
      if (locationIndex !== -1) {
        if (this.products[this.pickPage].locations[locationIndex].count === this.products[this.pickPage].locations[locationIndex].qty) {
          this.$buefy.toast.open({ message: 'จำนวนสินค้าใน location นี้หมดแล้วกรุณาตรวจสอบ', type: 'is-danger' })
        } else {
          this.incrementProductIndexCount({ productIndex: this.pickPage, productLocationIndex: locationIndex })
        }
      } else {
        this.$buefy.toast.open({ message: 'ไม่มีสินค้าในตำแหน่งนี้', type: 'is-danger' })
      }
    }
  }

  handleDecrement (locationIndex: number): void {
    this.decrementLocationCount({ productIndex: this.pickPage, locationIndex })
  }

  async getProductLocation (code: string): Promise<any> {
    try {
      const { data } = await this.stockLocationService.getProductLocation({
        warehouse: this.selectBranch.warehouse.id,
        code
      })
      return data.map((lo: any) => ({ ...lo, count: 0 }))
    } catch (error) {
      this.$buefy.toast.open({ message: 'การต้นหาข้อมูลตำแหน่งผิดพลาด', type: 'is-danger' })
      return []
    }
  }

  startPick (): void {
    const products: any[] = this.products.map((p: any, index: number) => ({
      ...p,
      nextStep: () => {
        if (index < this.products.length - 1) {
          this.locationsNowAlsoExistsInLocationsNext()
          this.setPickPage(this.pickPage + 1)
        } else {
          this.currentLocation = null
          this.setCurrentStep('summary')
        }
      }
    }))
    this.setProduct(products)
    this.setCurrentStep('pick')
  }

  deleteOrder (orderId: string): void {
    this.removeOrder(orderId)

    const products = this.products.flatMap((p: any) => {
      const orderQty = p.orders.find((order: any) => order.orderId === orderId)

      if (!orderQty) {
        return [p]
      }
      const newQty = p.qty - orderQty.qty

      if (newQty <= 0) {
        return []
      }
      return [{
        ...p,
        qty: newQty,
        orders: p.orders.filter((order: any) => order.orderId !== orderId)
      }]
    })
    this.setProduct(products)
  }

  stockLocationAdjustment (): void {
    const productSkus = this.products.flatMap((product: any) => product.locations.filter((loc: any) => loc.count > 0).map((loc: any) => ({
      code: product.code,
      location: loc.location.id,
      qty: loc.count
    })))
    this.$buefy.dialog.confirm({
      message: 'หยิบเสร็จแล้วใช่หรือไม่',
      canCancel: true,
      cancelText: 'หยิบต่อ',
      confirmText: 'เสร็จ',
      type: 'is-success',
      onConfirm: async () => {
        if (productSkus.length) {
          try {
            await this.stockLocationService.decrementMappingQty({
              warehouse: this.selectBranch.warehouse.id,
              productSkus
            })
            this.$buefy.toast.open({ message: 'บันทึกสำเร็จ', type: 'is-success' })
            this.setDefault()
          } catch (error) {
            this.$buefy.toast.open({ message: 'เกิดข้อผิดพลาด', type: 'is-danger' })
          }
        } else {
          this.$buefy.toast.open({ message: 'ไม่มีรายการที่สามารถนำไปอัพเดตข้อมูลได้', type: 'is-danger' })
        }
      }
    })
  }

  locationsNowAlsoExistsInLocationsNext (): void {
    const stayingOnNextLocation = this.products[this.pickPage + 1].locations.some((next: any) => next.location.id === this.currentLocation?.id)
    if (!stayingOnNextLocation) {
      this.setCurrentLocation(null)
    }
  }

  locationsNowAlsoExistsInPreviousLocations (): void {
    const stayingOnNextLocation = this.products[this.pickPage - 1]?.locations.some((next: any) => next.location.id === this.currentLocation?.id)
    if (!stayingOnNextLocation) {
      this.setCurrentLocation(null)
    }
  }

  goNext (): void {
    if (this.pickPage < this.products.length - 1) {
      this.locationsNowAlsoExistsInLocationsNext()
      this.setPickPage(this.pickPage + 1)
    } else {
      this.setCurrentLocation(null)
      this.setCurrentStep('summary')
    }
  }

  backPick (): void {
    if (this.currentStep === 'summary') {
      this.setPickPage(this.products.length - 1)
      this.setCurrentStep('pick')
    } else if (this.pickPage > 0) {
      this.locationsNowAlsoExistsInPreviousLocations()
      this.setPickPage(this.pickPage - 1)
    }
  }

  onCancel (): void {
    this.setDefault()
  }
}
